import React, { useState } from "react";
import styled from "styled-components";
import { difference, get, isArray, map, pickBy } from "lodash";
import moment from "moment";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import {
  Circle,
  CircleCheckmark,
  CirclePlus,
  CircleX,
} from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { BatchJobType } from "@sonder/ui/source/styled/components/ToastMessage";
import { ButtonList } from "@sonder/ui/source/styled/fragments/ButtonList";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import { CheckboxIcon } from "@sonder/ui/source/styled/fragments/CheckboxIcon";
import { HeaderBar } from "@sonder/ui/source/styled/fragments/HeaderBar";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { LabeledIconButton } from "@sonder/ui/source/styled/fragments/LabeledIconButton";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import {
  Scroller,
  ScrollerContent,
} from "@sonder/ui/source/styled/fragments/Scroller";
import { useSegment } from "analytics/segment";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import {
  TagSubtypes,
  TagTypes,
  TagsToIcons,
  TomTags,
  useGetTagsQuery,
} from "core/queries/GetTags";
import { UpdateTasksResponse, useUpdateTasks } from "core/queries/UpdateTasks";
import { addBatchJob, deselectTasks } from "tasks/actions";
import {
  AssigneePicker,
  AssigneePickerTrigger,
} from "tasks/components/AssigneePicker";
import { PriorityBadge } from "tasks/components/PriorityBadge";
import { SquadPicker } from "tasks/components/SquadPicker";
import { TagPicker } from "tasks/components/TagPicker";
import {
  TaskDueByDate,
  TaskDueByDateTrigger,
} from "tasks/components/TaskDueByDate";
import { TaskPriority } from "tasks/components/TaskPriority";
import { BatchedTasksType } from "tasks/constants";
import { useStore } from "tasks/store";
import { FilterStatus } from "typings/globalGQLTypes";

const StyledHeaderBar = styled(HeaderBar)`
  justify-content: flex-start;
`;

const StyledPriorityButton = styled(LabeledIconButton)`
  &:hover {
    & > span:first-child {
      border-style: solid;
    }
  }
`;

const StyledStatusButton = styled(LabeledIconButton)`
  justify-content: flex-start;
`;

interface BulkActionsHeaderProps {
  totalSelectedTasks: number;
}

export const BulkActionsHeader = ({
  totalSelectedTasks = 0,
}: BulkActionsHeaderProps) => {
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const {
    dispatch,
    state: { selectedTasks },
  } = useStore();
  const taskId = "VARIOUS";
  const eventName = "bulk_action";
  const [dueAt, setDueAt] = useState<moment.Moment | null | undefined>();
  const [priority, setPriority] = useState<number | null | undefined>();
  const [assigneeId, setAssigneeId] = useState<string | null | undefined>();
  const [squad, setSquad] = useState<TomTags[] | null | undefined>();
  const [globalTags, setGlobalTags] = useState<TomTags[] | null | undefined>();
  const [status, setStatus] = useState<FilterStatus | null | undefined>();
  const {
    hovered: isDeselectHovered,
    onMouseEnter: onDeselectEnter,
    onMouseLeave: onDeselectLeave,
  } = useHovered();
  const {
    hovered: isPriorityHovered,
    onMouseEnter: onPriorityEnter,
    onMouseLeave: onPriorityLeave,
  } = useHovered();
  const {
    hovered: isSquadHovered,
    onMouseEnter: onSquadEnter,
    onMouseLeave: onSquadLeave,
  } = useHovered();
  const {
    hovered: isTagHovered,
    onMouseEnter: onTagEnter,
    onMouseLeave: onTagLeave,
  } = useHovered();
  const {
    hovered: isStatusHovered,
    onMouseEnter: onStatusEnter,
    onMouseLeave: onStatusLeave,
  } = useHovered();
  const { data: squadData, loading: squadLoading } = useGetTagsQuery({
    variables: {
      tagType: TagTypes.Squad,
    },
  });
  const squadTags = get(squadData, "tomTags") || [];
  const canSubmit =
    dueAt !== undefined ||
    priority !== undefined ||
    assigneeId !== undefined ||
    squad !== undefined ||
    (globalTags || []).length > 0 ||
    status !== undefined;

  const [statusIcon, statusCopy] = (() => {
    switch (status) {
      case FilterStatus.complete:
        return [<CircleCheckmark />, "Complete"];
      case FilterStatus.ready:
        return [<Circle />, "Ready"];
      default:
        return [undefined, "Set Status"];
    }
  })();
  const [onUpdateTasks, { loading }] = useUpdateTasks({
    refetchQueries: [],
    onCompleted: (data: UpdateTasksResponse) => {
      const batchJobId = get(data, "updateTasks.batchJob.id");
      if (batchJobId) dispatch(addBatchJob(batchJobId, BatchJobType.Update));
    },
  });
  // TODO: Handle Bulk Multi Assign
  const assigneeIds = assigneeId ? [assigneeId] : [];
  return (
    <Scroller>
      <ScrollerContent>
        <StyledHeaderBar>
          <InputGroup>
            <LabeledIconButton
              title="Select / Deselect"
              onClick={() => {
                track({
                  eventName,
                  action: "click",
                  element: "deselectAllTasks",
                  props: {
                    taskId,
                  },
                });
                dispatch(deselectTasks(selectedTasks));
              }}
              onMouseEnter={onDeselectEnter}
              onMouseLeave={onDeselectLeave}
            >
              <CheckboxIcon checked={!isDeselectHovered} />
              <span>{`(${totalSelectedTasks}) Selected`}</span>
            </LabeledIconButton>
            <TaskPriority
              alignContent="left"
              eventName={eventName}
              priority={priority || null}
              taskId={taskId}
              trigger={
                <StyledPriorityButton
                  title="Priority"
                  onMouseEnter={onPriorityEnter}
                  onMouseLeave={onPriorityLeave}
                >
                  <PriorityBadge priority={priority || null} />
                  <InputGroup>
                    <span>Priority</span>
                    {priority !== undefined && isPriorityHovered && (
                      <CancelButton
                        onClick={e => {
                          track({
                            eventName,
                            action: "click",
                            element: "priority_selector.clear",
                            props: {
                              taskId,
                            },
                          });
                          setPriority(undefined);
                          e.stopPropagation();
                        }}
                      >
                        <CircleX />
                      </CancelButton>
                    )}
                  </InputGroup>
                </StyledPriorityButton>
              }
              onSetPriority={newPriority => setPriority(newPriority)}
            />
            <TaskDueByDate
              isTurnoverDisabled
              alignContent="left"
              bookingInfo={null}
              date={dueAt || null}
              eventName={eventName}
              futureCheckouts={[]}
              recurrenceSetting={null}
              renderTrigger={props => <TaskDueByDateTrigger {...props} />}
              taskId={taskId}
              onChange={variables =>
                setDueAt(get(variables, ["dueBy"], undefined))
              }
            />
            <AssigneePicker
              alignContent="left"
              assigneeIds={assigneeIds}
              eventName={eventName}
              renderTrigger={({ assignees, onClear }) => (
                <AssigneePickerTrigger
                  assignees={assignees}
                  onClear={onClear}
                />
              )}
              taskCityId={undefined}
              taskId={taskId}
              taskType={BatchedTasksType}
              onAssign={nextValue => {
                if (isArray(nextValue)) {
                  // TODO: Bulk Assign
                } else setAssigneeId(nextValue || undefined);
              }}
            />
            {!squadLoading && squadData && (
              <SquadPicker
                alignContent="left"
                renderTrigger={({ selectedTag, onClear }) => {
                  const SelectedTagIcon = TagsToIcons(
                    (selectedTag && selectedTag.name) || TagSubtypes.Unset,
                  );
                  const selectedTagName =
                    (selectedTag && selectedTag.name) || "Set Squad";
                  return (
                    <LabeledIconButton
                      data-testid="squad-picker-trigger"
                      onMouseEnter={onSquadEnter}
                      onMouseLeave={onSquadLeave}
                    >
                      <InputGroup>
                        {SelectedTagIcon && <SelectedTagIcon />}
                        <span>{selectedTagName}</span>
                        {selectedTag && selectedTag.name && isSquadHovered && (
                          <CancelButton
                            onClick={e => {
                              track({
                                eventName,
                                action: "click",
                                element: "squad_picker.clear",
                                props: {
                                  taskId,
                                },
                              });
                              if (onClear) onClear();

                              e.stopPropagation();
                            }}
                          >
                            <CircleX />
                          </CancelButton>
                        )}
                      </InputGroup>
                    </LabeledIconButton>
                  );
                }}
                tags={squad || []}
                onChange={newTags => {
                  if (!newTags.length) setSquad(undefined);
                  else setSquad(newTags);
                }}
              />
            )}
            <TagPicker
              alignContent="left"
              renderTrigger={({ selectedTags, onClear }) => {
                return (
                  <LabeledIconButton
                    data-testid="tag-picker-trigger"
                    onMouseEnter={onTagEnter}
                    onMouseLeave={onTagLeave}
                  >
                    <InputGroup>
                      <CirclePlus />
                      <span>
                        {selectedTags.length > 0
                          ? `Adding ${selectedTags.length} ${
                              selectedTags.length > 1 ? "tags" : "tag"
                            }`
                          : "Add Tags"}
                      </span>
                      {selectedTags.length > 0 && isTagHovered && (
                        <CancelButton
                          onClick={e => {
                            track({
                              eventName,
                              action: "click",
                              element: "tag_picker.clear",
                              props: {
                                taskId,
                              },
                            });
                            if (onClear) onClear();
                            e.stopPropagation();
                          }}
                        >
                          <CircleX />
                        </CancelButton>
                      )}
                    </InputGroup>
                  </LabeledIconButton>
                );
              }}
              tags={globalTags || []}
              onChange={data => {
                const { addTags, removeTags } = data;
                const newGlobalTags = [
                  ...(globalTags || []).filter(
                    tag =>
                      !removeTags.find(removedTag => removedTag.id === tag.id),
                  ),
                  ...addTags,
                ];
                setGlobalTags(newGlobalTags);
              }}
            />
            <Dropdown
              trigger={
                <StyledStatusButton
                  data-testid="status-dropdown-trigger"
                  title={statusCopy as string}
                  onMouseEnter={onStatusEnter}
                  onMouseLeave={onStatusLeave}
                >
                  <InputGroup>
                    {statusIcon}
                    <span>{statusCopy}</span>
                    {status !== undefined && isStatusHovered && (
                      <CancelButton
                        onClick={e => {
                          track({
                            eventName,
                            action: "click",
                            element: "status_selector.clear",
                            props: {
                              taskId,
                            },
                          });
                          setStatus(undefined);
                          e.stopPropagation();
                        }}
                      >
                        <CircleX />
                      </CancelButton>
                    )}
                  </InputGroup>
                </StyledStatusButton>
              }
            >
              {({ isOpen, closePortal }) => {
                return (
                  isOpen && (
                    <DropdownContent
                      align="left"
                      data-testid="status-dropdown-content"
                    >
                      <ButtonList>
                        <StyledStatusButton
                          title="Complete"
                          onClick={() => {
                            setStatus(FilterStatus.complete);
                            closePortal();
                            track({
                              eventName,
                              action: "click",
                              element: `status_${FilterStatus.complete}`,
                              props: {
                                taskId,
                              },
                            });
                          }}
                        >
                          <CircleCheckmark />
                          <span>Complete</span>
                        </StyledStatusButton>
                        <StyledStatusButton
                          title="Ready"
                          onClick={() => {
                            setStatus(FilterStatus.ready);
                            closePortal();
                            track({
                              eventName,
                              action: "click",
                              element: `status_${FilterStatus.ready}`,
                              props: {
                                taskId,
                              },
                            });
                          }}
                        >
                          <Circle />
                          <span>Ready</span>
                        </StyledStatusButton>
                      </ButtonList>
                    </DropdownContent>
                  )
                );
              }}
            </Dropdown>
            <PrimaryButton
              disabled={loading || !canSubmit}
              onClick={async () => {
                try {
                  const variables = pickBy(
                    {
                      assigneeId,
                      dueAt,
                      priority,
                      status,
                    },
                    // Omit `undefined`. `null`, `false`, and `0` are coo.
                    p => p !== undefined,
                  );
                  const removeTags = [
                    ...(squad ? map(difference(squadTags, squad), "id") : []),
                  ];
                  const addTags = [
                    ...map(squad || [], "id"),
                    ...map(globalTags || [], "id"),
                  ];
                  await onUpdateTasks({
                    variables: {
                      taskIds: selectedTasks.map(task => task.split("|")[1]),
                      ...variables,
                      addTags,
                      removeTags,
                    },
                  });
                  track({
                    eventName,
                    action: "click",
                    element: "submit_button",
                    props: {
                      taskId,
                    },
                  });
                  dispatch(deselectTasks(selectedTasks));
                } catch (error) {
                  errorReporter.notify(error);
                }
              }}
            >
              {loading ? "Saving..." : "Confirm"}
            </PrimaryButton>
          </InputGroup>
        </StyledHeaderBar>
      </ScrollerContent>
    </Scroller>
  );
};
